import React, { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

/**
 * Searchable select. options: [{ value, label, count? }]
 */
export default function SearchSelect({ value, onChange, options, placeholder = "Select", searchPlaceholder = "Search…", width = 180, label }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const current = options.find((o) => o.value === value);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => String(o.label).toLowerCase().includes(s)) : options;
  }, [options, q]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          style={{
            width, height: 36, borderRadius: 9, padding: "0 10px 0 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
            border: `1.5px solid ${open ? "hsl(var(--primary))" : "hsl(var(--input))"}`, background: "hsl(var(--card))", color: "hsl(var(--foreground))",
            fontSize: 12.5, fontFamily: "'IBM Plex Sans',sans-serif", cursor: "pointer", transition: "border-color .15s",
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>
            {label && <span style={{ color: "hsl(var(--muted-foreground))" }}>{label}: </span>}
            {current ? current.label : placeholder}
          </span>
          <ChevronDown size={14} style={{ opacity: 0.55, flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" style={{ width: Math.max(width, 220), padding: 6 }}>
        <div style={{ position: "relative", marginBottom: 6 }}>
          <Search size={13} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "hsl(var(--muted-foreground))" }} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            style={{ width: "100%", height: 32, paddingLeft: 28, paddingRight: 8, borderRadius: 8, border: "1px solid hsl(var(--input))", background: "hsl(var(--muted))", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", boxSizing: "border-box", fontFamily: "'IBM Plex Sans',sans-serif" }}
          />
        </div>
        <div style={{ maxHeight: 240, overflowY: "auto" }}>
          {shown.length === 0 && <div style={{ padding: "10px 8px", fontSize: 12, color: "hsl(var(--muted-foreground))" }}>No matches</div>}
          {shown.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => { onChange(o.value); setOpen(false); }}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", borderRadius: 7, border: "none", cursor: "pointer", textAlign: "left",
                  background: active ? "hsl(var(--primary) / 0.12)" : "transparent", color: active ? "hsl(var(--primary))" : "hsl(var(--foreground))",
                  fontSize: 12.5, fontWeight: active ? 600 : 400, fontFamily: "'IBM Plex Sans',sans-serif",
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "hsl(var(--muted))"; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ flex: 1 }}>{o.label}</span>
                {o.count != null && <span className="num" style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{o.count}</span>}
                {active && <Check size={13} />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
